/**
 * Whether ink can print a colour a screen can show.
 *
 * A screen makes colour from light and a press from ink on paper, and the two
 * reach different places. The vivid greens, oranges and blues of a colour
 * spectrum are the classic casualties: pick #00FF00 and the press gives back
 * something nearer a leaf. A customer ought to hear that while choosing, not
 * when the box of cards arrives.
 *
 * The honest way to answer is the printer's ICC profile, and the press file
 * does go through one - in Ghostscript, at the end. That is not available in a
 * browser at the moment of choosing, so this is a model of a press instead:
 * the Yule-Nielsen modified Neugebauer model, which predicts a halftone from
 * how its eight overprint colours look. Those eight are the process colours as
 * Adobe's own U.S. Web Coated conversion shows them (cyan #00AEEF, magenta
 * #EC008C, and so on), which is what every designer has seen in Illustrator.
 *
 * It is a warning light, not a proof. It decides one thing - is this colour
 * anywhere near what ink can do - and it is deliberately generous at the
 * border, because a warning that fires on ordinary colours is one nobody reads.
 */

import type { Ink } from '../model/types';

type Rgb = [ number, number, number ];

/** How the overprints look, in sRGB. Paper first, then C, M, Y, and their mixes. */
const PAPER: Rgb = [ 255, 255, 255 ];
const CYAN: Rgb = [ 0, 174, 239 ];
const MAGENTA: Rgb = [ 236, 0, 140 ];
const YELLOW: Rgb = [ 255, 242, 0 ];
const BLUE: Rgb = [ 46, 49, 146 ]; // C + M
const GREEN: Rgb = [ 0, 166, 81 ]; // C + Y
const RED: Rgb = [ 237, 28, 36 ]; // M + Y
const THREE: Rgb = [ 44, 40, 42 ]; // C + M + Y
const BLACK: Rgb = [ 35, 31, 32 ]; // K alone

/**
 * The Yule-Nielsen factor. Light scatters sideways in paper, so a halftone dot
 * darkens more than its area says; mixing in reflectance raised to 1/n rather
 * than in reflectance itself accounts for it. Two is the usual figure for
 * coated stock.
 */
const N = 2;

/**
 * Colour difference, in ΔE, beyond which a colour is out of reach.
 *
 * Ten is "clearly a different colour" to anyone looking at the two side by
 * side. Below it, the model's own approximations are as large as what it would
 * be warning about.
 */
export const OUT_OF_GAMUT = 10;

/* ---------------------------------------------------------------
 * sRGB, linear light, and Lab
 * ------------------------------------------------------------ */

const toLinear = ( v: number ) => {
  const c = v / 255;

  return c <= 0.04045 ? c / 12.92 : Math.pow( ( c + 0.055 ) / 1.055, 2.4 );
};

const fromLinear = ( v: number ) => {
  const c = Math.max( 0, Math.min( 1, v ) );

  return Math.round( 255 * ( c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow( c, 1 / 2.4 ) - 0.055 ) );
};

type Lab = [ number, number, number ];

function labOfLinear( [ r, g, b ]: Rgb ): Lab {
  // D65, which is sRGB's own white.
  const x = ( 0.4124 * r + 0.3576 * g + 0.1805 * b ) / 0.95047;
  const y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  const z = ( 0.0193 * r + 0.1192 * g + 0.9505 * b ) / 1.08883;

  const f = ( t: number ) => ( t > 216 / 24389 ? Math.cbrt( t ) : ( ( 24389 / 27 ) * t + 16 ) / 116 );

  return [ 116 * f( y ) - 16, 500 * ( f( x ) - f( y ) ), 200 * ( f( y ) - f( z ) ) ];
}

const distance = ( a: Lab, b: Lab ) => Math.hypot( a[ 0 ] - b[ 0 ], a[ 1 ] - b[ 1 ], a[ 2 ] - b[ 2 ] );

/* ---------------------------------------------------------------
 * The press
 * ------------------------------------------------------------ */

/** Each primary, in linear light raised to 1/n - the space the mixing happens in. */
const scaled = ( rgb: Rgb ): Rgb => rgb.map( ( v ) => Math.pow( toLinear( v ), 1 / N ) ) as Rgb;

const P = {
  paper: scaled( PAPER ),
  c: scaled( CYAN ),
  m: scaled( MAGENTA ),
  y: scaled( YELLOW ),
  cm: scaled( BLUE ),
  cy: scaled( GREEN ),
  my: scaled( RED ),
  cmy: scaled( THREE ),
};

/** How much light black ink lets back, as a factor on whatever is under it. */
const K_LETS = BLACK.map( toLinear ) as Rgb;

/**
 * How an ink recipe looks on paper, in linear light.
 *
 * The weights are Demichel's: the share of the paper covered by each overlap
 * of dots, if the three screens are laid down independently. Black is laid
 * over the result the same way.
 */
function printedLinear( [ c, m, y, k ]: Ink ): Rgb {
  const w = {
    paper: ( 1 - c ) * ( 1 - m ) * ( 1 - y ),
    c: c * ( 1 - m ) * ( 1 - y ),
    m: ( 1 - c ) * m * ( 1 - y ),
    y: ( 1 - c ) * ( 1 - m ) * y,
    cm: c * m * ( 1 - y ),
    cy: c * ( 1 - m ) * y,
    my: ( 1 - c ) * m * y,
    cmy: c * m * y,
  };

  const out: Rgb = [ 0, 0, 0 ];

  for ( let ch = 0; ch < 3; ch++ ) {
    let mixed = 0;

    for ( const key of Object.keys( w ) as Array<keyof typeof w> ) mixed += w[ key ] * ( P[ key ][ ch ] as number );

    // Black over it: the same colour, darkened, in the same scaled space.
    const under = mixed;
    const over = mixed * Math.pow( K_LETS[ ch ] as number, 1 / N );

    out[ ch ] = Math.pow( ( 1 - k ) * under + k * over, N );
  }

  return out;
}

/** How an ink recipe will look printed, as a hex colour for a chip. */
export function printedHex( ink: Ink ): string {
  const [ r, g, b ] = printedLinear( ink ).map( fromLinear );
  const pair = ( v: number | undefined ) => ( v ?? 0 ).toString( 16 ).padStart( 2, '0' );

  return `#${ pair( r ) }${ pair( g ) }${ pair( b ) }`;
}

/* ---------------------------------------------------------------
 * The question
 * ------------------------------------------------------------ */

export interface Reach {
  /** Whether ink can get near enough to this colour. */
  ok: boolean;
  /** How far the nearest ink is, in ΔE. */
  distance: number;
  /** The nearest recipe the model found, and how it would look. */
  ink: Ink;
  looks: string;
}

/**
 * The nearest printable colour to a screen colour, and how near it is.
 *
 * A coarse sweep of every recipe, then a refinement from the best of them.
 * The model is smooth and has no traps worth a cleverer search, and the whole
 * thing costs a few milliseconds - cheap enough to run as a spectrum is dragged.
 */
export function reach( hex: string ): Reach | null {
  const clean = hex.trim().replace( /^#/, '' );

  if ( ! /^[0-9a-f]{6}$/i.test( clean ) ) return null;

  const target = labOfLinear( [
    toLinear( parseInt( clean.slice( 0, 2 ), 16 ) ),
    toLinear( parseInt( clean.slice( 2, 4 ), 16 ) ),
    toLinear( parseInt( clean.slice( 4, 6 ), 16 ) ),
  ] );

  const cost = ( ink: Ink ) => distance( labOfLinear( printedLinear( ink ) ), target );

  let best: Ink = [ 0, 0, 0, 0 ];
  let bestCost = Infinity;

  for ( let c = 0; c <= 8; c++ ) {
    for ( let m = 0; m <= 8; m++ ) {
      for ( let y = 0; y <= 8; y++ ) {
        for ( let k = 0; k <= 4; k++ ) {
          const ink: Ink = [ c / 8, m / 8, y / 8, k / 4 ];
          const d = cost( ink );

          if ( d < bestCost ) {
            best = ink;
            bestCost = d;
          }
        }
      }
    }
  }

  // Then walk downhill, one ink at a time, halving the step as it settles.
  for ( let step = 1 / 16; step > 1 / 512; step /= 2 ) {
    let moved = true;

    while ( moved ) {
      moved = false;

      for ( let i = 0; i < 4; i++ ) {
        for ( const sign of [ -1, 1 ] ) {
          const next = best.slice() as Ink;

          next[ i ] = Math.min( 1, Math.max( 0, ( next[ i ] as number ) + sign * step ) );

          const d = cost( next );

          if ( d < bestCost - 1e-6 ) {
            best = next;
            bestCost = d;
            moved = true;
          }
        }
      }
    }
  }

  return {
    ok: bestCost <= OUT_OF_GAMUT,
    distance: Math.round( bestCost * 10 ) / 10,
    ink: best.map( ( v ) => Math.round( v * 1000 ) / 1000 ) as Ink,
    looks: printedHex( best ),
  };
}
